import styles from './style.module.scss'
import { PageTitle, Table, BackButton, ActionsDropdown } from '@Root/components'
import { useParams } from 'react-router-dom'
import { API } from '@Root/API'
import { useDispatch } from 'react-redux'
import { snackbarActions, modalActions } from '@Root/store'
import { errorMessage } from '@Root/helpers'
import { useState, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { confirmDeleteAction, runConfirmedBulkDelete } from '../lib/submissionActions'

const DELETE_COLUMN = {
  name: 'Delete',
  field: 'delete_action',
  searchInputType: 'textInput',
  cellType: 'link',
}

const BASE_COLUMNS = [
  {
    name: 'ID',
    field: 'id',
    searchInputType: 'textInput',
    cellType: 'link',
    cellLinkField: 'id_link',
  },
  {
    name: 'Cognito Entry ID',
    field: 'cognito_entry_id',
    searchInputType: 'textInput',
    cellType: 'text',
  },
  {
    name: 'Form Name',
    field: 'form_name',
    searchInputType: 'textInput',
    cellType: 'text',
  },
  {
    name: 'Status',
    field: 'status_label',
    searchInputType: 'textInput',
    cellType: 'text',
  },
  {
    name: 'Contact Email',
    field: 'contact_email',
    searchInputType: 'textInput',
    cellType: 'mailLink',
  },
  {
    name: 'Contact Name',
    field: 'contact_name',
    searchInputType: 'textInput',
    cellType: 'text',
  },
  {
    name: 'Created At',
    field: 'created_at',
    searchInputType: 'datePicker',
    cellType: 'text',
  },
  {
    name: 'Updated At',
    field: 'updated_at',
    searchInputType: 'datePicker',
    cellType: 'text',
  },
]

const TABLE_COLUMNS = [...BASE_COLUMNS, DELETE_COLUMN]

export const DetailsCognitoForms = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams()
  const [shouldBeUpdated, setShouldBeUpdated] = useState(false)
  const [checkedIds, setCheckedIds] = useState([])
  const [clearCheckedRowsSignal, setClearCheckedRowsSignal] = useState(0)
  const [isResyncing, setIsResyncing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [canDeleteSubmissions, setCanDeleteSubmissions] = useState(false)
  const [tableColumns, setTableColumns] = useState(BASE_COLUMNS)

  const onError = useCallback(
    error => {
      dispatch(snackbarActions.setSnackbar({ text: errorMessage(error), isError: true }))
    },
    [dispatch]
  )

  const onSuccess = useCallback(
    message => {
      dispatch(snackbarActions.setSuccessNotification({ text: message }))
    },
    [dispatch]
  )

  const refreshTable = useCallback(() => {
    setShouldBeUpdated(true)
    setTimeout(() => setShouldBeUpdated(false), 100)
  }, [])

  const clearCheckedRows = useCallback(() => {
    setCheckedIds([])
    setClearCheckedRowsSignal(signal => signal + 1)
  }, [])

  const fetchDataHandler = useCallback(
    async params => {
      try {
        const response = await API.cognitoForms.getAllSubmissions(id, params)
        const { data, meta, columns: responseColumns, can_delete_submissions: canDelete } = response.data

        const canDeleteSubmissionsFlag = Boolean(canDelete)
        setCanDeleteSubmissions(canDeleteSubmissionsFlag)
        setTableColumns(canDeleteSubmissionsFlag ? TABLE_COLUMNS : BASE_COLUMNS)

        const processedData = (data || []).map(row => ({
          ...row,
          parsed_data: row.parsed_data ? JSON.stringify(row.parsed_data, null, 2) : '',
        }))

        return {
          data: {
            data: processedData,
            meta,
            columns: responseColumns || (canDeleteSubmissionsFlag ? TABLE_COLUMNS : BASE_COLUMNS),
          },
        }
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    [id]
  )

  const handleDelete = useCallback(
    row => {
      if (!row.permissions?.delete_action?.edit) {
        onError(new Error('You have no permission to delete this submission'))
        return
      }

      const isSynced = row.is_synced

      confirmDeleteAction(dispatch, {
        text: isSynced
          ? `Submission #${row.id} is synced. Linked contact or application data will remain in the system.`
          : `Are you sure you want to delete submission #${row.id}?`,
        checkboxLabel: isSynced ? 'Delete synced submission' : null,
        requireCheckbox: isSynced,
      })
        .then(async includeSynced => {
          try {
            await API.cognitoForms.deleteSubmission(row.id, id, isSynced || includeSynced)
            clearCheckedRows()
            refreshTable()
            onSuccess(`Submission #${row.id} has been deleted successfully`)
          } catch (error) {
            onError(error)
          }
        })
        .catch(() => null)
        .finally(() => dispatch(modalActions.hideModal()))
    },
    [dispatch, id, clearCheckedRows, refreshTable, onError, onSuccess]
  )

  const handleDeleteSelected = async () => {
    if (!checkedIds.length || isDeleting) return

    setIsDeleting(true)

    try {
      const result = await runConfirmedBulkDelete({
        dispatch,
        formId: id,
        ids: checkedIds,
        confirmText: `Are you sure you want to delete ${checkedIds.length} submission(s)?`,
        deleteRequest: API.cognitoForms.deleteSubmissions,
        entityLabel: 'submission',
        onSuccess,
        onError,
      })

      if (result === null) {
        return
      }

      clearCheckedRows()
      refreshTable()
    } catch (error) {
      onError(error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleResyncSelected = async () => {
    if (!checkedIds.length || isResyncing) return
    setIsResyncing(true)
    let successCount = 0
    let failCount = 0

    for (const submissionId of checkedIds) {
      try {
        const res = await API.cognitoForms.resyncSubmission(submissionId)
        const result = res.data || res
        if (result.success) {
          successCount++
        } else {
          failCount++
        }
      } catch {
        failCount++
      }
    }

    setIsResyncing(false)
    clearCheckedRows()

    if (failCount > 0 && successCount === 0) {
      onError(new Error(`Resync failed for ${failCount} submission(s)`))
    } else if (failCount > 0) {
      onSuccess(`Resync completed: ${successCount} synced, ${failCount} failed`)
    } else {
      onSuccess(`Resync completed: ${successCount} submissions synced`)
    }

    refreshTable()
  }

  const actions = [
    {
      name: isResyncing ? 'Resyncing...' : `Resync Selected (${checkedIds.length})`,
      handler: handleResyncSelected,
      disabled: !checkedIds.length || isResyncing || isDeleting,
    },
    ...(canDeleteSubmissions
      ? [
          {
            name: isDeleting ? 'Deleting...' : `Delete Selected (${checkedIds.length})`,
            handler: handleDeleteSelected,
            disabled: !checkedIds.length || isResyncing || isDeleting,
          },
        ]
      : []),
  ]

  const onChooseAction = actionName => {
    const action = actions.find(a => a.name === actionName)
    if (action?.handler) action.handler()
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.titleWrap}>
        <BackButton text={'Forms'} clickHandler={() => history.push('/home/forms')} />
        <PageTitle title={'Submissions'} />
      </div>

      <div className={styles.actionsDropdownWrapper}>
        <ActionsDropdown actions={actions} clickHandler={onChooseAction} />
      </div>

      <Table
        key={id}
        name='cognito-submissions'
        columns={tableColumns}
        fetchDataHandler={fetchDataHandler}
        fetchColumnOptions={() => API.getColumnOptions('cognito-submissions')}
        fetchSaveColumnOptions={options => API.saveColumnOptions('cognito-submissions', options)}
        hasSorting={false}
        hasFilters={false}
        errorHandler={onError}
        successHandler={onSuccess}
        shouldBeUpdated={shouldBeUpdated}
        onCheckedRowsChange={setCheckedIds}
        clearCheckedRowsSignal={clearCheckedRowsSignal}
        clickLinkHandlers={{ delete_action: handleDelete }}
        styleConfig={{
          datePeriodDropdownStyle: {
            display: 'none',
          },
        }}
      />
    </div>
  )
}
